import { useRef, useState } from 'react';
import { TokenChips } from './TokenChips';
import { boxToTokens, tokensToBox } from './bannerTokens';
import { FIELD_INPUT, FieldLabel } from './bannerFormBits';
import type { BannerComposer } from './useBannerComposer';

const MAX_TITLE = 200;
const MAX_MESSAGE = 1000;

/** Title and message, shared by the two forms. Their limits are the API's. */
export function CopyFields({ composer, rows }: { composer: BannerComposer; rows: number }) {
  const { annTitle, setAnnTitle, annBody, setAnnBody, textTokens, audience } = composer;
  const { details } = audience;
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const [noRoom, setNoRoom] = useState(false);

  // The boxes say [Store Format]; what is kept and sent is the token. The limits are the
  // server's, on what it is sent, so a label that fits the box can still be too long.
  const shownTitle = tokensToBox(annTitle, details.tokens);
  const shownBody = tokensToBox(annBody, details.tokens);
  const typeTitle = (text: string) => {
    const next = boxToTokens(text, details.tokens);
    if (next.length <= MAX_TITLE) setAnnTitle(next);
  };
  const typeBody = (text: string) => {
    const next = boxToTokens(text, details.tokens);
    setNoRoom(next.length > MAX_MESSAGE);
    if (next.length <= MAX_MESSAGE) setAnnBody(next);
  };

  // At the cursor, or over what is selected. A value set from here gets past the box's own
  // limit, and half a token would reach the server as text, so one that does not fit waits.
  const insertToken = (token: string) => {
    const box = messageRef.current;
    const label = tokensToBox(token, details.tokens);
    const start = box?.selectionStart ?? shownBody.length;
    const end = box?.selectionEnd ?? shownBody.length;
    const next = boxToTokens(`${shownBody.slice(0, start)}${label}${shownBody.slice(end)}`, details.tokens);
    if (next.length > MAX_MESSAGE) {
      setNoRoom(true);
      return;
    }
    setNoRoom(false);
    setAnnBody(next);
    const cursor = start + label.length;
    requestAnimationFrame(() => {
      box?.focus();
      box?.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <>
      <div>
        <FieldLabel text="Title" htmlFor="banner-title" />
        <input
          id="banner-title"
          type="text"
          value={shownTitle}
          onChange={(e) => typeTitle(e.target.value)}
          maxLength={MAX_TITLE}
          placeholder="e.g. Congratulations on your promotion!"
          className={FIELD_INPUT}
        />
      </div>
      <div>
        <FieldLabel text="Message" htmlFor="banner-message">
          <span className="flex-none text-xs text-gray-400">
            {annBody.length}/{MAX_MESSAGE}
          </span>
        </FieldLabel>
        <textarea
          id="banner-message"
          ref={messageRef}
          value={shownBody}
          onChange={(e) => typeBody(e.target.value)}
          maxLength={MAX_MESSAGE}
          rows={rows}
          placeholder="e.g. Brilliant work in today's client meeting — the whole team noticed."
          className={`${FIELD_INPUT} block resize-y`}
        />
        <TokenChips tokens={textTokens} onInsert={insertToken} />
        {details.error && (
          <p className="mt-1.5 text-xs text-gray-500">
            The details to add could not be loaded.{' '}
            <button type="button" onClick={details.retry} className="font-medium text-primary-600 underline hover:text-primary-700">
              Try again
            </button>
          </p>
        )}
        {noRoom && <p className="mt-1 text-xs text-amber-700">No room left in the message for that detail.</p>}
      </div>
    </>
  );
}
